import { useNavigate } from 'react-router-dom'
import type { ManagerOrderedClient } from './types'

function formatRub(n: number): string {
  if (n === 0) return '—'
  if (Math.abs(n) >= 1_000_000) return `₽ ${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `₽ ${Math.round(n / 1_000)}K`
  return `₽ ${n.toFixed(2)}`
}

const CATEGORY_STYLES: Record<'A' | 'B' | 'C', string> = {
  A: 'bg-emerald-50 text-emerald-700',
  B: 'bg-amber-50 text-amber-700',
  C: 'bg-gray-100 text-gray-600',
}

/** Заказчики, перешедшие в заказ за выбранный период.
 *
 *  Источник суммы — orderedPrice × quantity по ORDERED-позициям, у которых
 *  последний переход в ORDERED попал в окно периода (та же логика, что в
 *  карточке «В заказе» KPI). Если у клиента несколько заявок с заказами —
 *  суммы складываются. */
export function OrderedClientsTable({ clients }: { clients: ManagerOrderedClient[] }) {
  const navigate = useNavigate()
  const totalSum = clients.reduce((acc, c) => acc + c.orderedSumRub, 0)

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-sm text-gray-500">
          Заказчики, перешедшие в заказ <span className="text-gray-400">({clients.length})</span>
        </div>
        {totalSum > 0 && (
          <div className="text-xs text-gray-500">
            Сумма заказов:{' '}
            <span className="text-gray-900 font-medium tabular-nums">{formatRub(totalSum)}</span>
          </div>
        )}
      </div>

      {clients.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">Нет заказов за период</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="text-left font-normal pb-2">Наименование</th>
                <th className="text-left font-normal pb-2">Кат.</th>
                <th className="text-left font-normal pb-2">Сегмент</th>
                <th className="text-left font-normal pb-2">Город</th>
                <th className="text-right font-normal pb-2">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr
                  key={c.clientId}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/clients/${c.clientId}`)}
                >
                  <td className="py-2 text-gray-900">
                    <div className="flex items-center gap-2">
                      <span>{c.clientName}</span>
                      {c.clientCode && (
                        <span className="text-xs text-gray-400">{c.clientCode}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2">
                    <span
                      className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-medium ${CATEGORY_STYLES[c.category]}`}
                    >
                      {c.category}
                    </span>
                  </td>
                  <td className="py-2 text-gray-600">{c.segment ?? '—'}</td>
                  <td className="py-2 text-gray-600">{c.city ?? '—'}</td>
                  <td className="py-2 text-right tabular-nums text-gray-900 font-medium">
                    {formatRub(c.orderedSumRub)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
